/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject, Input} from '@angular/core';
import * as _ from 'lodash';

import template from './line-item-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemBulkActionService} from './line-item-bulk-action.service';
import {LineItemListSetupService} from './line-item-list-setup.service';
import {TargetingHelper} from '../targeting/targeting-helper';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-line-item-list-page',
  template
})
export class LineItemListPageComponent extends AdminResourceListPageBase {
  @Input() parentObject: string;
  @Input() parentId: string;
  @Input() readOnly: boolean;

  readonly itemStateName = 'app.adv.line-items.line-item';
  readonly advertiserItemStateName = 'app.adv.advertisers.advertiser';
  readonly orderItemStateName = 'app.adv.orders.order';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly userItemStateName = 'app.adm.users.user';
  readonly creativeItemStateName = 'app.adv.creatives.creative';
  readonly layoutItemStateName = 'app.adm.layouts.layout';

  readonly childColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'layout', sortable: false},
    {param: 'width', label: 'dimensions', sortable: false},
    {param: '', label: 'info', sortable: false},
    {param: 'updateTime'},
    {param: '', label: 'targeting', sortable: false}
  ];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LineItemResource) private readonly LineItem: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly listService: AdnResourceListService,
    private readonly lineItemBulkActionService: LineItemBulkActionService,
    private readonly lineItemListSetupService: LineItemListSetupService
  ) {
    super(LocalNetworkProfile);
  }

  isFieldVisible(param: string): boolean {
    return this.isListFieldVisible(param);
  }

  get visibleColumns(): AdminListColumn[] {
    return this.normalizeColumns(this.sortHeaderParams);
  }

  filtersResolved(): boolean {
    const list = this.list;
    if (!list) {
      return true;
    }
    return !!(list.selectInQuery?.$resolved
      && list.dateQuery?.$resolved
      && list.targetQuery?.$resolved
      && list.teamQuery?.$resolved
      && list.countryTeamQuery?.$resolved);
  }

  toggleChildren(lineItem: any): void {
    this.list.getChildren?.(lineItem);
  }

  childList(lineItemId: string): any {
    return this.list.children?.[lineItemId];
  }

  businessLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    const perms = this.list?.perms || {};
    return perms.MANAGE_SYSTEM !== true
      && perms.AX_ADVERTISER_ADMIN !== true
      && perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  systemLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_SYSTEM !== true;
  }

  layoutLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_LAYOUTS !== true;
  }

  eCpmValue(lineItem: any): any {
    const amount = _.get(lineItem, ['spendDelivery', 'amount']);
    const currency = _.get(lineItem, ['spendDelivery', 'currency']);
    const rendered = lineItem?.renderedDelivery;
    if (!_.isFinite(amount) || !rendered) {
      return undefined;
    }
    return {
      amount: amount / rendered * 1000,
      currency
    };
  }

  cpcValue(lineItem: any): any {
    const amount = _.get(lineItem, ['spendDelivery', 'amount']);
    const currency = _.get(lineItem, ['spendDelivery', 'currency']);
    const clicks = lineItem?.clickDelivery;
    if (!_.isFinite(amount) || !clicks) {
      return undefined;
    }
    return {
      amount: amount / clicks,
      currency
    };
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.listService.ensureAngularList(this.list);
    this.list.canExpand = true;

    if (!this.list.__adnLineItemListCommonsWired) {
      Object.defineProperty(this.list, '__adnLineItemListCommonsWired', {value: true, enumerable: false});
      this.lineItemListSetupService.applyListCommons(this.list, false, 'LINE_ITEM_VIEW');
      if (_.isFunction(this.list.addAfterFill)) {
        this.list.addAfterFill(() => {
          this.lineItemListSetupService.applyListCommons(this.list, true, 'LINE_ITEM_VIEW');
        });
      }
    }

    if (!this.list.getChildren) {
      getListChildren(this.list, this.adnListHelper, OBJECT_TYPE.LineItem, this.Creative);
    }

    if (!this.list.bulkTargetingAction) {
      this.list.bulkTargetingAction = () => {
        const selTargetIds = _.map(_.filter(ADN_TARGETING_TYPES, (tt: any) => {
          return !tt.bulkBlocked;
        }), 'id');

        this.targetingEditorModalService.open({
          lineItemType: 'NORMAL',
          modelLineItem: {},
          pageType: 'bulk',
          reachEnabled: 'LINE_ITEM',
          mandatoryTargets: undefined,
          overridableTargeting: undefined,
          availableTargets: _.pick(ADN_TARGETING_TYPES, selTargetIds),
          targetingMode: null,
          targeting: {},
          isLockedDown: false
        }).then((wc: any) => {
          if (!wc) {
            return;
          }
          const newTargeting = wc.wc;
          let updateProps: string[] = [];
          const lineItems = _.map(this.list.bulkSelection, (v: any, k: string) => {
            const obj: any = {id: k};

            if (wc.reset) {
              obj.targeting = newTargeting;
            } else {
              const currentTargets = TargetingHelper.getTargetsWithTargets(wc, ['wc']);
              const apiProps = _.uniq(_.map(currentTargets, (ct: any) => ct.targets));
              updateProps = _.uniq(_.map(currentTargets, (ct: any) => ct.apiType));
              obj.targeting = _.pick(newTargeting, apiProps);
              obj.targeting = _.merge(v.targeting, obj.targeting);
            }
            return obj;
          });
          this.list.miscBulkAction(null, {}, lineItems, this.LineItem, {
            updateOnlyTheseTargetTypes: updateProps,
            ignoreProduct: this.LocalNetworkProfile.getDefaults()['defaultIgnoreMarketplaceTargeting']
          });
        });
      };
    }

    if (!this.list.bulkAction) {
      this.list.bulkAction = this.lineItemBulkActionService.createBulkAction(this.list);
    }
  }
}
